import { insightOutput, lifetimeOutput, pendingOutput, progressOutput, recoveryOutput, reportOutput, retrospectiveOutput, TRAINING_ROUTES, } from "@repo/contract/training";
import { getJson, withQuery, } from "../core/request";
/**
 * `GET /me/progress` — the Progress screen's aggregates (T33): weekly volume
 * and sessions, this week's sets per target muscle against the plan, the
 * month's totals, and the strength lines. What each number means is the
 * server's definition (`@repo/training`, `core/model/progress.ts`) — the
 * screen draws it and never recomputes it.
 */
export function fetchProgress(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.progress, {
        locale: options.locale,
        weeks: options.weeks,
    }), progressOutput, init);
}
/**
 * `GET /me/retrospective` — the Retrospectiva's six cards for one ISO week
 * (T34). `ready: false` with `cards: null` is an answer, not an error: the
 * week is not over, or had no ended session. What each card means is the
 * server's definition (`@repo/training`, `core/model/retrospective.ts`).
 */
export function fetchRetrospective(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.retrospective, {
        locale: options.locale,
        month: options.month,
        // `week` is the server's default, so it is never written: the weekly
        // story's request stays byte for byte what it was.
        period: options.period === "week" ? undefined : options.period,
        week: options.week,
        year: options.year,
    }), retrospectiveOutput, init);
}
/**
 * `GET /me/recovery` — muscle readiness (`D203`): the muscles the last week
 * of sets touched, least recovered first. A model of the log, not of the
 * body — `@repo/training`'s `core/model/recovery.ts` says what is in it.
 */
export function fetchRecovery(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.recovery, { locale: options.locale }), recoveryOutput, init);
}
/**
 * `GET /me/lifetime` — every workout's totals and the achievements
 * (`D204`). It reads every set the person has, so Progress asks for it and
 * Today never does.
 */
export function fetchLifetime(api, init) {
    return getJson(api, TRAINING_ROUTES.lifetime, lifetimeOutput, init);
}
/**
 * `GET /me/insight` — the Today screen's one insight card (T36), or
 * `insight: null` when no rule fires. Which rule wins is the server's
 * (`@repo/training`, `core/model/insight.ts`); the card renders
 * `messageKey` with `params` and never re-decides.
 */
export function fetchInsight(api, options = {}, init) {
    return getJson(api, withQuery(TRAINING_ROUTES.insight, {
        language: options.language,
        locale: options.locale,
    }), insightOutput, init);
}
/**
 * `GET /me/pending` — the four waiting signals in one read (T38): the
 * check-in, the unread trainer reply, the ready Retrospectiva and the draft.
 * Every one is the server's definition; `retrospectiveReady` stays until the
 * week rolls over, so dismissing it is the device's (nothing stores
 * "opened").
 */
export function fetchPending(api, init) {
    return getJson(api, TRAINING_ROUTES.pending, pendingOutput, init);
}
/**
 * `GET /me/report` — the weekly or monthly progress report (`D252`).
 * `ready: false` with `report: null` is an answer, not an error: the span
 * is still running, or came before the first workout. What each figure
 * means is the server's (`@repo/training`, `core/model/progress-report.ts`).
 */
export function fetchProgressReport(api, options = {}, init) {
    const month = options.period === "month";
    return getJson(api, withQuery(TRAINING_ROUTES.report, {
        locale: options.locale,
        // Only the name the period reads: the query is strict, and a stray
        // `week` beside `month` would be a second key for the same answer.
        month: month ? options.month : undefined,
        period: month ? "month" : undefined,
        week: month ? undefined : options.week,
    }), reportOutput, init);
}
//# sourceMappingURL=api.js.map